import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { ReactElement, ReactNode, RefObject } from 'react';
import { useNavigate } from 'react-router';

import {
  BaseUrlRoute,
  LanguageRoute,
  PersonsRoute,
  VerifyAdminRoute,
} from '@/config/router/routes';
import type { AppState } from '@/domain/models/appState';
import { VerifyAdminType } from '@/presentation/pages/verifyAdmin/verifyAdminType';
import { appStateStore, useAppState } from '@/presentation/stores/appStateStore';
import { BackgroundWrapper } from '@/presentation/widgets/BackgroundWrapper';
import { PrimaryIconButtonRounded } from '@/presentation/widgets/PrimaryIconButton';
import { SafeArea } from '@/presentation/widgets/SafeArea';
import { SnackbarManager } from '@/presentation/widgets/snackbar/snackbarManager';
import {
  DiagnosticsLog,
  type DiagnosticsEntry,
} from '@/utils/diagnostics/diagnosticsLog';
import { StorageHelper } from '@/utils/storageHelper';

import {
  DiagnosticsActionButton,
  DiagnosticsTextButton,
} from './DiagnosticsButtons';
import {
  DIAGNOSTICS_FILTER_LABEL,
  DIAGNOSTICS_FILTER_VALUES,
  DiagnosticsFilter,
  diagnosticsFilterMatches,
} from './diagnosticsFilter';
import { EntryTile } from './EntryTile';
import {
  StatusPanel,
  buildStatusRows,
  currentStt,
  formatTime,
} from './StatusPanel';
import styles from './DiagnosticsPage.module.css';

/**
 * Журнал для разработчика: состояние планшета и последние события.
 *
 * Вход — 5 тапов по версии на экране языка и PIN. Тексты на русском: экран
 * для разработчика, не для посетителя. Секретов не показывает: токены и
 * ключи — только «есть/нет» и время выдачи.
 *
 * Кнопка «Перезапустить приложение» из Dart-версии убрана вместе с пакетом
 * `restart_app`: перезапуск процесса — Android-функция, в браузере её нет.
 */

/** `static const _confirmWindow = Duration(seconds: 3)` */
const CONFIRM_WINDOW_MS = 3000;

/** Имена опасных действий, ждущих второго нажатия (`_armed`). */
const ARMED_SESSION = 'session';
const ARMED_RESET = 'reset';

/** Пустой набор одной ссылкой: состояние заменяется, а не меняется. */
const EMPTY_EXPANDED: ReadonlySet<DiagnosticsEntry> = new Set();

/** Взведённое действие и момент, когда его взвели. */
interface ArmedAction {
  readonly action: string;
  readonly at: number;
}

/**
 * `mounted` из State: страницу могли закрыть, пока шёл запрос.
 *
 * Значение ставится в эффекте, а не только в cleanup: в StrictMode React
 * монтирует дерево дважды, и без этого ref остался бы `false`.
 */
function useMountedRef(): RefObject<boolean> {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return mounted;
}

/**
 * `Clipboard.setData`. В браузере буфер обмена доступен только в защищённом
 * контексте (https или localhost) и может быть запрещён разрешениями —
 * вызов отказывает, и страница обязана это показать.
 */
async function writeToClipboard(text: string): Promise<void> {
  const clipboard: Clipboard | undefined = navigator.clipboard;
  if (clipboard === undefined) {
    throw new Error('буфер обмена недоступен: нужен https или localhost');
  }
  await clipboard.writeText(text);
}

/** `_confirmed(action)`: второе нажатие в течение окна — подтверждение. */
function useConfirm(): {
  armed: string | null;
  confirmed: (action: string) => boolean;
} {
  const [armed, setArmed] = useState<ArmedAction | null>(null);
  const confirmed = useCallback(
    (action: string): boolean => {
      const now = Date.now();
      if (
        armed !== null &&
        armed.action === action &&
        now - armed.at < CONFIRM_WINDOW_MS
      ) {
        // В Dart взвод снимался без setState; здесь перерисовка всё равно
        // случится, но экран сразу уходит на другой маршрут.
        setArmed(null);
        return true;
      }
      setArmed({ action, at: now });
      SnackbarManager.instance.showWarning({
        title: 'Нажмите ещё раз, чтобы подтвердить',
      });
      return false;
    },
    [armed],
  );
  return { armed: armed?.action ?? null, confirmed };
}

/** Три исхода «Перечитать конфиг»: тост выбирается по наличию ключа STT. */
function showReloadResult(): void {
  // `ref.read` после await — состояние берётся заново, а не из замыкания.
  const stt = currentStt(appStateStore.getState().appState.person);
  const noKey = stt?.apiUrl != null && stt?.apiKey == null;
  const message =
    stt?.apiUrl == null
      ? 'Конфиг обновлён. STT у аватара не настроен'
      : noKey
        ? 'Конфиг обновлён, но ключа STT нет — сбросьте сессию и войдите по PIN'
        : 'Конфиг обновлён, ключ STT есть';
  // В журнал результат попадёт как тост.
  if (noKey) {
    SnackbarManager.instance.showWarning({ title: message });
  } else {
    SnackbarManager.instance.showSuccess({ title: message });
  }
}

/**
 * `_reloadConfig`: заново открытый конфиг + защищённый (ключи STT), с
 * цепочкой токенов. Сразу после входа по PIN токены свежие — это и лечит
 * STT-401.
 */
function useReloadConfig(mounted: RefObject<boolean>): {
  busy: boolean;
  reloadConfig: () => Promise<void>;
} {
  const [busy, setBusy] = useState(false);
  const reloadConfig = useCallback(async (): Promise<void> => {
    if (busy) return;
    if (StorageHelper.instance.baseUrl === null) {
      SnackbarManager.instance.showError({ title: 'Сервер не задан' });
      return;
    }
    setBusy(true);
    DiagnosticsLog.instance.event('действие: перечитать конфиг и ключи');
    try {
      const ok = await appStateStore.getState().checkAndSetBaseUrl();
      if (!mounted.current || !ok) return;
      showReloadResult();
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [busy, mounted]);
  return { busy, reloadConfig };
}

/** Действия, уводящие с экрана. */
interface NavigationActions {
  readonly resetSession: () => void;
  readonly changePerson: () => void;
  readonly changeServer: () => void;
  readonly fullReset: () => void;
  readonly back: () => void;
}

function useNavigationActions(
  mounted: RefObject<boolean>,
  confirmed: (action: string) => boolean,
): NavigationActions {
  const navigate = useNavigate();

  /**
   * Стирает токены и ведёт на PIN: верный PIN выдаст новую сессию и
   * вернёт сюда, дальше — «Перечитать конфиг».
   */
  const resetSession = useCallback(async (): Promise<void> => {
    if (!confirmed(ARMED_SESSION)) return;
    await StorageHelper.instance.clearTabletCredentials();
    DiagnosticsLog.instance.event('действие: сессия сброшена, вход по PIN');
    if (!mounted.current) return;
    VerifyAdminRoute.go(navigate, VerifyAdminType.diagnostics);
  }, [confirmed, mounted, navigate]);

  /** Всё, кроме журнала: адрес, PIN, токены, конфиг, аватар, язык. */
  const fullReset = useCallback(async (): Promise<void> => {
    if (!confirmed(ARMED_RESET)) return;
    await StorageHelper.instance.removeAll();
    DiagnosticsLog.instance.event('действие: полный сброс данных');
    await DiagnosticsLog.instance.flush();
    if (!mounted.current) return;
    BaseUrlRoute.go(navigate);
  }, [confirmed, mounted, navigate]);

  const changePerson = useCallback((): void => {
    DiagnosticsLog.instance.event('действие: смена аватара');
    PersonsRoute.go(navigate);
  }, [navigate]);

  const changeServer = useCallback((): void => {
    DiagnosticsLog.instance.event('действие: смена сервера или планшета');
    BaseUrlRoute.go(navigate);
  }, [navigate]);

  const back = useCallback((): void => {
    LanguageRoute.go(navigate);
  }, [navigate]);

  return { resetSession, changePerson, changeServer, fullReset, back };
}

/** Раскрытые строки журнала и то, что делают кнопки подвала. */
interface LogActions {
  readonly expanded: ReadonlySet<DiagnosticsEntry>;
  readonly toggle: (entry: DiagnosticsEntry) => void;
  readonly copy: () => void;
  readonly clear: () => void;
}

function useLogActions(appState: AppState): LogActions {
  const [expanded, setExpanded] =
    useState<ReadonlySet<DiagnosticsEntry>>(EMPTY_EXPANDED);

  const toggle = useCallback((entry: DiagnosticsEntry): void => {
    setExpanded((current) => {
      // Набор пересобирается, прежний не меняется.
      const next = new Set(current);
      if (!next.delete(entry)) next.add(entry);
      return next;
    });
  }, []);

  /** `_copy()`: состояние строками, пустая строка, весь журнал. */
  const copy = useCallback(async (): Promise<void> => {
    const status = buildStatusRows(appState)
      .map((row) => `${row.name}: ${row.value}`)
      .join('\n');
    try {
      await writeToClipboard(`${status}\n\n${DiagnosticsLog.instance.export()}`);
      SnackbarManager.instance.showSuccess({ title: 'Журнал скопирован' });
    } catch (e) {
      // В Dart `Clipboard.setData` не отказывал, в браузере — может.
      SnackbarManager.instance.showError({
        title: 'Журнал не скопирован',
        description: String(e),
      });
    }
  }, [appState]);

  const clear = useCallback((): void => {
    DiagnosticsLog.instance.clear();
    setExpanded(EMPTY_EXPANDED);
  }, []);

  return { expanded, toggle, copy, clear };
}

interface ActionButtonsProps {
  readonly busy: boolean;
  readonly armed: string | null;
  readonly reloadConfig: () => void;
  readonly nav: NavigationActions;
}

/** Кнопки в панели состояния. */
function actionButtons({
  busy,
  armed,
  reloadConfig,
  nav,
}: ActionButtonsProps): ReactNode[] {
  return [
    <DiagnosticsActionButton
      label="Перечитать конфиг и ключи STT"
      hint="после PIN — лечит STT-401"
      busy={busy}
      onPressed={reloadConfig}
    />,
    <DiagnosticsActionButton
      label="Сбросить сессию и войти по PIN"
      hint="стирает токены; затем «Перечитать»"
      armed={armed === ARMED_SESSION}
      onPressed={nav.resetSession}
    />,
    <DiagnosticsActionButton
      label="Сменить аватар"
      onPressed={nav.changePerson}
    />,
    <DiagnosticsActionButton
      label="Сменить сервер или планшет"
      onPressed={nav.changeServer}
    />,
    <DiagnosticsActionButton
      label="Полный сброс данных"
      hint="адрес, PIN, токены, конфиг — всё заново"
      armed={armed === ARMED_RESET}
      danger
      onPressed={nav.fullReset}
    />,
  ];
}

interface FooterActionsProps {
  readonly filter: DiagnosticsFilter;
  readonly onFilter: (filter: DiagnosticsFilter) => void;
  readonly onBack: () => void;
  readonly onCopy: () => void;
  readonly onClear: () => void;
}

/** `footerActions` BackgroundWrapper: назад, копирование, очистка, фильтры. */
function footerActions({
  filter,
  onFilter,
  onBack,
  onCopy,
  onClear,
}: FooterActionsProps): ReactNode[] {
  return [
    <PrimaryIconButtonRounded icon="arrowLeft" onPressed={onBack} />,
    <DiagnosticsTextButton label="Скопировать" onPressed={onCopy} />,
    <DiagnosticsTextButton label="Очистить" onPressed={onClear} />,
    // `Spacer()`: инлайновый стиль перебивает `flex-shrink: 0`, который
    // BackgroundWrapper ставит всем детям ряда.
    <div style={{ flex: 1 }} />,
    ...DIAGNOSTICS_FILTER_VALUES.map((value) => (
      <DiagnosticsTextButton
        label={DIAGNOSTICS_FILTER_LABEL[value]}
        selected={value === filter}
        onPressed={() => onFilter(value)}
      />
    )),
  ];
}

interface LogListProps {
  readonly filter: DiagnosticsFilter;
  readonly expanded: ReadonlySet<DiagnosticsEntry>;
  readonly onToggle: (entry: DiagnosticsEntry) => void;
}

/**
 * `ListenableBuilder(listenable: DiagnosticsLog.instance)`.
 *
 * Подписка живёт здесь, а не на странице: в Dart новая запись перерисовывала
 * только список, панель состояния оставалась прежней до следующего setState.
 */
function LogList({ filter, expanded, onToggle }: LogListProps): ReactElement {
  const entries = useSyncExternalStore(
    DiagnosticsLog.instance.subscribe,
    DiagnosticsLog.instance.getSnapshot,
  );
  const visible = entries.filter((entry) =>
    diagnosticsFilterMatches(filter, entry),
  );
  if (visible.length === 0) {
    return <div className={styles.empty}>Записей нет</div>;
  }
  return (
    <div className={styles.list}>
      {visible.map((entry, index) => (
        <EntryTile
          key={index}
          entry={entry}
          time={formatTime(entry.time)}
          expanded={expanded.has(entry)}
          onTap={() => onToggle(entry)}
        />
      ))}
    </div>
  );
}

export function DiagnosticsPage(): ReactElement {
  const appState = useAppState();
  const mounted = useMountedRef();
  const [filter, setFilter] = useState<DiagnosticsFilter>(DiagnosticsFilter.all);
  const { armed, confirmed } = useConfirm();
  const { busy, reloadConfig } = useReloadConfig(mounted);
  const nav = useNavigationActions(mounted, confirmed);
  const log = useLogActions(appState);

  return (
    <BackgroundWrapper
      footerActions={footerActions({
        filter,
        onFilter: setFilter,
        onBack: nav.back,
        onCopy: log.copy,
        onClear: log.clear,
      })}
    >
      <SafeArea className={styles.page}>
        {/* Снизу — место под панель кнопок BackgroundWrapper, она лежит
            поверх содержимого. */}
        <div className={styles.padding}>
          <div className={styles.row}>
            <StatusPanel
              className={styles.status}
              rows={buildStatusRows(appState)}
              actions={actionButtons({ busy, armed, reloadConfig, nav })}
            />
            <div className={styles.log}>
              <LogList
                filter={filter}
                expanded={log.expanded}
                onToggle={log.toggle}
              />
            </div>
          </div>
        </div>
      </SafeArea>
    </BackgroundWrapper>
  );
}
